import TransportAcceptance from "../components/TransportAcceptance";
import InfantSeatSelector from '../components/InfantSeatSelector';
import { buildInfantSeatServices, getInfantSeatCount, INFANT_SEAT_PRICE } from '../utils/infantSeats';
import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import NewOrderFormV2 from "@/components/NewOrderFormV2";
import ExtendedWaitingSelector from "@/components/ExtendedWaitingSelector";
import { getLocalizedAreaName } from "../components/new-order-form-v2/constants";
import MyPage from "@/pages/MyPage";
import SupplierStatusBadge from "@/pages/supplier/SupplierStatusBadge";
import SupplierOrdersWorkspace, { supplierWorkspaceCopy } from './supplier/SupplierOrdersWorkspace';
import NewBookingSummary from './supplier/NewBookingSummary';
import { batchWorkspaceCopy } from './supplier/batchWorkspaceCopy';
import './supplier/SupplierPortal.css';
import './supplier/SupplierBookingForm.css';
import { parseAdminOrderBatchText } from "@/components/admin/parseAdminOrderText";
import { API_BASE_URL as API } from "../apiConfig";
import {
  EXTRA_SERVICE_PRICES,
  INITIAL_EDIT_EXTRA_SERVICES,
  OVERTIME_RATES,
  formatDestinationDisplay as formatDestinationDisplayForLanguage,
  formatPickupDisplay as formatPickupDisplayForLanguage,
  buildSupplierOrdersCsv,
  buildSupplierOrderSections,
  getFilteredAndSortedOrders,
  getSupplierPaymentStatusLabel,
  getSupplierOrderDisplayPrice,
  isOrderIncomplete,
  normalizeVehicle,
} from "./supplierDashboardUtils";
import { getOrderNo } from "../utils/orderDisplay";
import { getTokyoDateKey } from "../utils/tokyoDate";
import {
  EXTENDED_WAITING_SERVICE_TYPE,
  getExtendedWaitingDisplay,
  getExtendedWaitingUnitPrice,
} from "../utils/extendedWaiting";
import shonanPalmLogo from "../assets/shonan-palm-gold-transparent.png";

const createIdempotencyKey = () => (
  globalThis.crypto?.randomUUID?.() || `supplier-order-${Date.now()}-${Math.random().toString(36).slice(2)}`
);


import {
  AlertTriangle,
  ChevronRight,
  CheckCircle2,
  Download,
  FileText,
  LogOut,
  MapPin,
  Plus,
  Sparkles,
  Trash2,
  UserRound,
  X,
  Sun,
  Moon,
  MoreHorizontal,
  Pencil,
  Check,
  ChevronDown,
} from "lucide-react";

export default function SupplierDashboard({ user, onLogout, onUserUpdate }) {
  const { t, getVehicleName, language } = useLanguage();
  const [activeTab, setActiveTab] = useState("orders");
  const [supplierTheme, setSupplierTheme] = useState(() => { try { return localStorage.getItem('shonan_supplier_theme') === 'dark' ? 'dark' : 'light'; } catch { return 'light'; } });
  const workspaceCopy = supplierWorkspaceCopy[language] || supplierWorkspaceCopy.en;
  const [departureDateToFilter, setDepartureDateToFilter] = useState('');
  const [missingOnly, setMissingOnly] = useState(false);
  const [bookingPreview, setBookingPreview] = useState(null);
  const newBookingActionsRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  
  // Filter and sort states for orders
  const [hideCompleted, setHideCompleted] = useState(false);
  const [hideCancelled, setHideCancelled] = useState(false);
  const [orderSearchFilter, setOrderSearchFilter] = useState("");
  const [customerCodeFilter, setCustomerCodeFilter] = useState("");
  const [customerNameFilter, setCustomerNameFilter] = useState("");
  const [departureDateFilter, setDepartureDateFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("asc"); // Service date and pickup time, earliest first.
  const [orderGroupFilter, setOrderGroupFilter] = useState("all");
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("all");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("all");

  // Bulk delete state
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  const [batchOrderText, setBatchOrderText] = useState("");
  const [batchOrderItems, setBatchOrderItems] = useState([]);
  const [batchSelectedIds,setBatchSelectedIds] = useState([]);
  const [batchView,setBatchView] = useState('all');
  const [batchCustomerCode,setBatchCustomerCode] = useState('');
  const [batchRetryLocked,setBatchRetryLocked] = useState(false);
  const batchAttemptRef = useRef(null);
  const batchEditSnapshotsRef = useRef({});
  const batchSubmitBusyRef = useRef(false);
  const [batchOrderError, setBatchOrderError] = useState("");
  const [batchOrderSubmitting, setBatchOrderSubmitting] = useState(false);
  const batchSubmissionKeyRef = useRef(null);

  useEffect(() => {
    fetchOrders();
    fetchRoutes();
    fetchAreas();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error("获取订单失败:", error);
    }
  };

  const fetchRoutes = async () => {
    try {
      const response = await axios.get(`${API}/routes`);
      setRoutes(response.data);
    } catch (error) {
      console.error("获取路线失败:", error);
    }
  };

  const fetchAreas = async () => {
    try {
      const response = await axios.get(`${API}/config/areas`);
      setAreas((response.data || []).filter(area => area.enabled !== false));
    } catch (error) {
      console.error("获取地区失败:", error);
    }
  };

  const formatPickupDisplay = (order) => formatPickupDisplayForLanguage(order, language);
  const formatDestinationDisplay = (order) => formatDestinationDisplayForLanguage(order, language);
  const supplierBatchCopies = {
    zh: {
      tab: "供应商批量下单",
      title: "供应商批量下单",
      description: "直接粘贴多天预约文本，系统会先拆成批量清单；请逐单核对后再创建订单。",
      detectedCount: (count) => `已识别 ${count} 个订单`,
      readySummary: (ready, incomplete) => `可创建 ${ready} / 待补充 ${incomplete}`,
      pasteLabel: "粘贴订单内容",
      pasteRequired: "请先粘贴订单内容",
      noParsedOrders: "没有识别出可创建的订单，请检查格式或手动下单",
      parseButton: "批量识别",
      parseHint: "识别只负责填字段，不会直接创建订单。",
      detectFirst: "请先识别订单",
      incompleteOrder: (index, fields) => `第 ${index + 1} 个订单信息不完整，请先补齐：${fields.join("、")}`,
      partialFailure: (created, failed) => `已创建 ${created} 个订单，失败 ${failed} 个，请检查失败项后重试。`,
      success: (count) => `批量创建成功：${count} 个订单`,
      createFailed: "批量创建失败",
      detected: "已识别",
      ready: "已就绪",
      incomplete: "待补充",
      completeYellow: "补齐所有黄色项目后再创建。",
      orderTitle: (index) => `第 ${index + 1} 个订单`,
      needs: (fields) => `需补充：${fields.join("、")}`,
      canCreate: "可创建",
      recognized: (fields) => `已识别：${fields.join(" / ")}`,
      remove: "移除",
      service: "服务",
      transfer: "单程接送",
      charter: "包车",
      date: "日期",
      time: "时间",
      vehicle: "车型",
      pickupArea: "出发地区",
      dropoffArea: "目的地区域",
      selectArea: "请选择地区",
      pickupDetail: "出发地详细地址",
      dropoffDetail: "目的地详细地址",
      customerCode: "客户代码/订单号",
      customerName: "客户名称",
      passengers: "人数",
      luggage: "行李",
      charterHours: "包车时长",
      endLocation: "结束地点",
      creating: "创建中...",
      createOrders: "批量创建订单",
      missingFields: {
        departure_date: "日期",
        pickup_area: "出发地区",
        dropoff_area: "目的地区域",
        vehicle_type: "车型",
        pickup_location_detail: "出发地详细地址",
        dropoff_location_detail: "目的地详细地址",
      },
    },
    ja: {
      tab: "代理店一括予約",
      title: "代理店一括予約",
      description: "複数日の予約テキストを貼り付けると、システムが一括リストに分割します。各予約を確認してから作成してください。",
      detectedCount: (count) => `${count}件の予約を認識済み`,
      readySummary: (ready, incomplete) => `作成可能 ${ready} / 要補完 ${incomplete}`,
      pasteLabel: "予約内容を貼り付け",
      pasteRequired: "予約内容を貼り付けてください",
      noParsedOrders: "作成できる予約を認識できませんでした。形式を確認するか、手動で予約してください",
      parseButton: "一括認識",
      parseHint: "認識は項目入力のみです。直接予約は作成されません。",
      detectFirst: "先に予約を認識してください",
      incompleteOrder: (index, fields) => `${index + 1}件目の予約情報が不足しています：${fields.join("、")}`,
      partialFailure: (created, failed) => `${created}件作成済み、${failed}件失敗しました。失敗項目を確認して再試行してください。`,
      success: (count) => `一括作成が完了しました：${count}件`,
      createFailed: "一括作成に失敗しました",
      detected: "認識済み",
      ready: "作成可能",
      incomplete: "要補完",
      completeYellow: "黄色の項目をすべて補完してから作成してください。",
      orderTitle: (index) => `${index + 1}件目の予約`,
      needs: (fields) => `要補完：${fields.join("、")}`,
      canCreate: "作成可能",
      recognized: (fields) => `認識済み：${fields.join(" / ")}`,
      remove: "削除",
      service: "サービス",
      transfer: "片道送迎",
      charter: "貸切",
      date: "日付",
      time: "時間",
      vehicle: "車種",
      pickupArea: "出発エリア",
      dropoffArea: "目的地エリア",
      selectArea: "エリアを選択",
      pickupDetail: "出発地詳細住所",
      dropoffDetail: "目的地詳細住所",
      customerCode: "顧客コード/予約番号",
      customerName: "顧客名",
      passengers: "人数",
      luggage: "荷物",
      charterHours: "貸切時間",
      endLocation: "終了地点",
      creating: "作成中...",
      createOrders: "一括作成",
      missingFields: {
        departure_date: "日付",
        pickup_area: "出発エリア",
        dropoff_area: "目的地エリア",
        vehicle_type: "車種",
        pickup_location_detail: "出発地詳細住所",
        dropoff_location_detail: "目的地詳細住所",
      },
    },
    en: {
      tab: "Supplier Batch Orders",
      title: "Supplier Batch Orders",
      description: "Paste multi-day reservation text. The system will split it into a batch list; review each order before creating.",
      detectedCount: (count) => `${count} orders detected`,
      readySummary: (ready, incomplete) => `Ready ${ready} / Missing ${incomplete}`,
      pasteLabel: "Paste order content",
      pasteRequired: "Please paste order content first",
      noParsedOrders: "No creatable orders were detected. Check the format or create orders manually.",
      parseButton: "Detect Batch",
      parseHint: "Detection only fills fields. It will not create orders directly.",
      detectFirst: "Please detect orders first",
      incompleteOrder: (index, fields) => `Order ${index + 1} is incomplete. Please fill: ${fields.join(", ")}`,
      partialFailure: (created, failed) => `${created} orders created, ${failed} failed. Check failed items and retry.`,
      success: (count) => `Batch created successfully: ${count} orders`,
      createFailed: "Batch creation failed",
      detected: "Detected",
      ready: "Ready",
      incomplete: "Missing",
      completeYellow: "Fill all yellow items before creating.",
      orderTitle: (index) => `Order ${index + 1}`,
      needs: (fields) => `Missing: ${fields.join(", ")}`,
      canCreate: "Ready",
      recognized: (fields) => `Detected: ${fields.join(" / ")}`,
      remove: "Remove",
      service: "Service",
      transfer: "Transfer",
      charter: "Charter",
      date: "Date",
      time: "Time",
      vehicle: "Vehicle",
      pickupArea: "Pickup area",
      dropoffArea: "Destination area",
      selectArea: "Select area",
      pickupDetail: "Pickup detailed address",
      dropoffDetail: "Destination detailed address",
      customerCode: "Customer code / order no.",
      customerName: "Customer name",
      passengers: "Passengers",
      luggage: "Luggage",
      charterHours: "Charter hours",
      endLocation: "End location",
      creating: "Creating...",
      createOrders: "Create Batch Orders",
      missingFields: {
        departure_date: "Date",
        pickup_area: "Pickup area",
        dropoff_area: "Destination area",
        vehicle_type: "Vehicle",
        pickup_location_detail: "Pickup detailed address",
        dropoff_location_detail: "Destination detailed address",
      },
    },
    es: {
      tab: "Reservas en lote",
      title: "Reservas en lote",
      description: "Pegue el texto de reservas de varios días. El sistema lo separará en una lista; revise cada reserva antes de crearla.",
      detectedCount: (count) => `${count} reservas detectadas`,
      readySummary: (ready, incomplete) => `Listas ${ready} / Faltan datos ${incomplete}`,
      pasteLabel: "Pegar contenido de reservas",
      pasteRequired: "Pegue primero el contenido de las reservas",
      noParsedOrders: "No se detectaron reservas que se puedan crear. Revise el formato o cree la reserva manualmente.",
      parseButton: "Detectar lote",
      parseHint: "La detección solo rellena campos. No crea reservas directamente.",
      detectFirst: "Detecte las reservas primero",
      incompleteOrder: (index, fields) => `La reserva ${index + 1} está incompleta. Complete: ${fields.join(", ")}`,
      partialFailure: (created, failed) => `${created} reservas creadas, ${failed} fallidas. Revise los fallos e intente de nuevo.`,
      success: (count) => `Lote creado correctamente: ${count} reservas`,
      createFailed: "Error al crear el lote",
      detected: "Detectadas",
      ready: "Listas",
      incomplete: "Faltan datos",
      completeYellow: "Complete todos los campos amarillos antes de crear.",
      orderTitle: (index) => `Reserva ${index + 1}`,
      needs: (fields) => `Falta: ${fields.join(", ")}`,
      canCreate: "Lista",
      recognized: (fields) => `Detectado: ${fields.join(" / ")}`,
      remove: "Eliminar",
      service: "Servicio",
      transfer: "Traslado",
      charter: "Charter",
      date: "Fecha",
      time: "Hora",
      vehicle: "Vehículo",
      pickupArea: "Zona de recogida",
      dropoffArea: "Zona de destino",
      selectArea: "Seleccione zona",
      pickupDetail: "Dirección detallada de recogida",
      dropoffDetail: "Dirección detallada de destino",
      customerCode: "Código de cliente / reserva",
      customerName: "Nombre del cliente",
      passengers: "Pasajeros",
      luggage: "Equipaje",
      charterHours: "Horas de charter",
      endLocation: "Lugar final",
      creating: "Creando...",
      createOrders: "Crear reservas en lote",
      missingFields: {
        departure_date: "Fecha",
        pickup_area: "Zona de recogida",
        dropoff_area: "Zona de destino",
        vehicle_type: "Vehículo",
        pickup_location_detail: "Dirección detallada de recogida",
        dropoff_location_detail: "Dirección detallada de destino",
      },
    },
  };
  const batchCopy = supplierBatchCopies[language] || supplierBatchCopies.en;
  const batchUi = batchWorkspaceCopy[language] || batchWorkspaceCopy.en;
  const supplierPaymentT = (key) => key === "supplierPaymentPaid" ? t('supplierPaymentPaid') : t(key);
  const vehicleFilterOptions = useMemo(() => {
    const vehicles = Array.from(new Set(orders.map((order) => normalizeVehicle(order.vehicle_type)).filter(Boolean)));
    return ["all", ...vehicles];
  }, [orders]);

  const filteredOrders = getFilteredAndSortedOrders({
    orders,
    hideCompleted,
    hideCancelled,
    orderSearchFilter,
    customerCodeFilter,
    customerNameFilter,
    departureDateFilter,
    departureDateToFilter,
    sortOrder,
    vehicleTypeFilter,
    serviceTypeFilter,
  });

  const orderSections = useMemo(
    () => buildSupplierOrderSections(filteredOrders, sortOrder),
    [filteredOrders, sortOrder]
  );

  const statusOrders = useMemo(
    () => {
      if (orderGroupFilter === "completed") {
        return orderSections.completed;
      }
      if (orderGroupFilter === "ongoing") {
        return [...orderSections.today, ...orderSections.tomorrow];
      }
      if (orderGroupFilter === "not_started") {
        return orderSections.not_started;
      }
      return [
        ...orderSections.completed,
        ...orderSections.today,
        ...orderSections.tomorrow,
        ...orderSections.not_started,
      ];
    },
    [orderGroupFilter, orderSections]
  );
  const visibleOrders = missingOnly ? statusOrders.filter(order => isOrderIncomplete(order)) : statusOrders;

  const getBatchOrderMissingFields = (item) => {
    const requiredFields = [
      ["departure_date", batchCopy.missingFields.departure_date],
      ["pickup_area", batchCopy.missingFields.pickup_area],
      ["dropoff_area", batchCopy.missingFields.dropoff_area],
      ["vehicle_type", batchCopy.missingFields.vehicle_type],
      ["pickup_location_detail", batchCopy.missingFields.pickup_location_detail],
      ["dropoff_location_detail", batchCopy.missingFields.dropoff_location_detail],
    ];
    return requiredFields
      .filter(([field]) => !item?.[field])
      .map(([, label]) => label);
  };

  const batchReadyCount = batchOrderItems.filter(item => getBatchOrderMissingFields(item).length === 0).length;
  const batchIncompleteCount = Math.max(0, batchOrderItems.length - batchReadyCount);
  const selectedReadyBatchItems = batchOrderItems.filter(item=>batchSelectedIds.includes(item.batch_item_id) && !getBatchOrderMissingFields(item).length);

  const handleAcceptQuote = async (orderId) => {
    try {
      const order = orders.find((o) => o.id === orderId);
      if (!order) return;

      const quoteResponse = await axios.get(`${API}/quotes/order/${orderId}`);
      const quote = quoteResponse.data;

      await axios.post(`${API}/quotes/${quote.id}/accept`);
      alert(t('acceptQuote') + " ✓");
      fetchOrders();
    } catch (error) {
      alert(error.response?.data?.detail || t('operationFailed'));
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm(t('confirmCancel'))) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      alert(t('cancelSuccess'));
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      alert(error.response?.data?.detail || t('operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Duplicate order - 复制订单
  const handleDuplicateOrder = async (order) => {
    const confirmMsg = language === 'zh' 
      ? '确定要复制此订单吗？将创建一个相同的新订单。'
      : language === 'ja'
      ? 'この注文をコピーしますか？同じ内容の新しい注文が作成されます。'
      : language === 'es'
      ? '¿Desea copiar esta reserva? Se creará una nueva reserva con los mismos datos.'
      : 'Copy this order? A new order with the same details will be created.';
    
    if (!window.confirm(confirmMsg)) return;
    
    setLoading(true);
    try {
      // 使用不带admin的API路径
      const response = await axios.post(`${API}/orders/${order.id}/duplicate`);
      const successMsg = language === 'zh'
        ? `订单复制成功！新订单号: ${response.data.new_order_no || getOrderNo(response.data.order)}`
        : language === 'ja'
        ? `注文のコピーが完了しました！新しい注文番号: ${response.data.new_order_no || getOrderNo(response.data.order)}`
        : language === 'es'
        ? `¡Reserva copiada correctamente! Nuevo número de reserva: ${response.data.new_order_no || getOrderNo(response.data.order)}`
        : `Order copied successfully! New order number: ${response.data.new_order_no || getOrderNo(response.data.order)}`;
      alert(successMsg);
      setSelectedOrder(null);
      fetchOrders();
      // 自动打开编辑新订单
      if (response.data.order) {
        setTimeout(() => {
          handleEditOrder(response.data.order);
        }, 500);
      }
    } catch (error) {
      alert(error.response?.data?.detail || t('operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const buildReturnOrderPatch = (order) => ({
    pickup_area: order.dropoff_area || order.pickup_area || "",
    dropoff_area: order.pickup_area || order.dropoff_area || "",
    pickup_location: order.destination || order.dropoff_location_detail || order.dropoff_area || "",
    destination: order.pickup_location || order.pickup_location_detail || order.pickup_area || "",
    pickup_location_detail: order.dropoff_location_detail || order.destination || "",
    dropoff_location_detail: order.pickup_location_detail || order.pickup_location || "",
    pickup_terminal: order.dropoff_terminal || "",
    dropoff_terminal: order.pickup_terminal || "",
  });

  const handleCreateReturnOrder = async (order) => {
    const confirmMsg = language === 'zh'
      ? '确定要生成返程订单吗？系统会复制此订单并自动调换上车地和目的地。'
      : language === 'ja'
      ? '復路の予約を作成しますか？この予約をコピーして乗車地と目的地を入れ替えます。'
      : language === 'es'
      ? '¿Desea crear una reserva de regreso? Se copiará la reserva intercambiando la recogida y el destino.'
      : 'Create a return order? This will copy the order and swap pickup and destination.';

    if (!window.confirm(confirmMsg)) return;

    setLoading(true);
    try {
      const duplicateResponse = await axios.post(`${API}/orders/${order.id}/duplicate`);
      const newOrder = duplicateResponse.data.order;
      if (!newOrder?.id) {
        throw new Error(language === 'zh' ? '复制订单失败' : language === 'ja' ? '予約のコピーに失敗しました' : language === 'es' ? 'No se pudo copiar la reserva' : 'Failed to copy order');
      }

      const returnPatch = buildReturnOrderPatch(order);
      await axios.put(`${API}/orders/${newOrder.id}`, returnPatch);
      const returnOrder = { ...newOrder, ...returnPatch };
      const successMsg = language === 'zh'
        ? `返程订单已生成！新订单号: ${duplicateResponse.data.new_order_no || getOrderNo(returnOrder)}`
        : language === 'ja'
        ? `復路予約を作成しました！新しい予約番号: ${duplicateResponse.data.new_order_no || getOrderNo(returnOrder)}`
        : language === 'es'
        ? `¡Reserva de regreso creada! Nuevo número de reserva: ${duplicateResponse.data.new_order_no || getOrderNo(returnOrder)}`
        : `Return order created! New order number: ${duplicateResponse.data.new_order_no || getOrderNo(returnOrder)}`;
      alert(successMsg);
      setSelectedOrder(null);
      fetchOrders();
      setTimeout(() => {
        handleEditOrder(returnOrder);
      }, 500);
    } catch (error) {
      alert(error.response?.data?.detail || error.message || t('operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Bulk delete orders
  const handleBulkDelete = async () => {
    if (selectedOrderIds.length === 0) {
      alert(language === 'zh' ? '请先选择要删除的订单' : language === 'ja' ? '削除する注文を選択してください' : language === 'es' ? 'Seleccione primero las reservas que desea cancelar' : 'Please select orders to delete');
      return;
    }
    
    const confirmMsg = language === 'zh' 
      ? `确定要取消选中的 ${selectedOrderIds.length} 个订单吗？` 
      : language === 'ja'
      ? `選択した ${selectedOrderIds.length} 件の注文をキャンセルしますか？`
      : language === 'es'
      ? `¿Desea cancelar las ${selectedOrderIds.length} reservas seleccionadas?`
      : `Are you sure you want to cancel ${selectedOrderIds.length} selected orders?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders/bulk-delete`, {
        order_ids: selectedOrderIds
      });
      alert(response.data.message);
      setSelectedOrderIds([]);
      fetchOrders();
    } catch (error) {
      alert(error.response?.data?.detail || t('operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Toggle select order
  const toggleSelectOrder = (orderId) => {
    setSelectedOrderIds(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const selectSupplierOrderGroup = (groupId) => {
    if (!groupId) return;
    const groupIds = orders.filter(order => order.booking_group_id === groupId).map(order => order.id).filter(Boolean);
    setSelectedOrderIds(prev => Array.from(new Set([...prev, ...groupIds])));
  };

  // Toggle select all
  const toggleSelectAll = () => {
    const allVisibleSelected = visibleOrders.length > 0 && visibleOrders.every((order) => selectedOrderIds.includes(order.id));
    if (allVisibleSelected) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(visibleOrders.map((order) => order.id));
    }
  };

  const getSupplierMissingFieldLabel = (field) => {
    const labels = {
      pickup_location: t('pickupLocation'),
      destination: t('destination'),
      pickup_time: t('pickupTime'),
      customer_name: t('customerName'),
      departure_date: t('departureDate'),
      vehicle_type: t('vehicleType'),
    };
    return labels[field] || field;
  };

  const isSupplierImportantException = (order, incompleteFields = null) => Boolean(
    incompleteFields ||
    order?.driver_issue_unresolved ||
    order?.driver_complaint_unresolved ||
    ["pending_quote", "pending_confirmation", "pending_admin_confirmation"].includes(String(order?.status || "").toLowerCase())
  );

  const handleExportVisibleOrders = () => {
    if (!visibleOrders.length) {
      alert(t('supplierExportNoOrders'));
      return;
    }
    const csv = buildSupplierOrdersCsv(visibleOrders, {
      getVehicleName,
      formatPickupDisplay,
      formatDestinationDisplay,
      labels: {
        orderNumber: t('orderNumber'),
        customerName: t('customerName'),
        customerCode: t('customerCode'),
        status: t('status'),
        departureDate: t('departureDate'),
        pickupTime: t('pickupTime'),
        pickupLocation: t('pickupLocation'),
        destination: t('destination'),
        vehicleType: t('vehicleType'),
        passengerCount: t('passengerCount'),
        luggageCount: t('luggageCount'),
        notes: t('supplierNotesAndContact'),
      },
    });
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const date = getTokyoDateKey();
    link.href = url;
    link.download = `${t('supplierExportFilename')}-${date}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  // 编辑订单时的额外服务状态
  const [editExtraServices, setEditExtraServices] = useState(INITIAL_EDIT_EXTRA_SERVICES);
  const [editOtherServiceDesc, setEditOtherServiceDesc] = useState("");
  const [editExtendedWaitingQuantity, setEditExtendedWaitingQuantity] = useState(0);
  const [transferPricing, setTransferPricing] = useState([]); // Transfer订单价格数据
  const [charterPricing, setCharterPricing] = useState([]); // Charter订单价格数据
  const [editVehiclePriceCache, setEditVehiclePriceCache] = useState({}); // 缓存各车型价格

  // 获取transfer_pricing数据
  const fetchTransferPricing = async () => {
    try {
      const response = await axios.get(`${API}/config/transfer-pricing`);
      setTransferPricing(response.data);
    } catch (error) {
      console.error("获取接送价格失败:", error);
    }
  };

  // 获取charter_pricing数据
  const fetchCharterPricing = async () => {
    try {
      const response = await axios.get(`${API}/config/charter-pricing`);
      setCharterPricing(response.data);
    } catch (error) {
      console.error("获取包车价格失败:", error);
    }
  };

  useEffect(() => {
    fetchTransferPricing();
    fetchCharterPricing();
  }, []);

  const handleEditOrder = (order) => {
    setSelectedOrder(order);

    // 获取原始计费信息
    const priceSnapshot = order.price_snapshot || {};
    const originalSelectedHours = order.selected_hours || 5;
    const pickupExtraHours = priceSnapshot.pickup_extra_hours || 0;
    const endLocationExtraHours = priceSnapshot.end_location_extra_hours || 0;
    
    // 初始化基本表单
    setEditForm({
      customer_name: order.customer_name || "",
      phone: order.phone || "",
      departure_date: order.departure_date || "",
      pickup_location: order.pickup_location || "",
      destination: order.destination || "",
      pickup_time: order.pickup_time || "",
      passenger_count: order.passenger_count || 1,
      luggage_count: order.luggage_count || 0,
      infant_seat_count: getInfantSeatCount(order),
      trip_details: order.trip_details || "",
      vehicle_type: normalizeVehicle(order.vehicle_type),
      // 保存原始价格信息用于计算
      original_base_price: order.base_price || 0,
      original_vehicle_type: normalizeVehicle(order.vehicle_type),
      // 包车时间相关
      selected_hours: originalSelectedHours,
      original_selected_hours: originalSelectedHours,
      pickup_extra_hours: pickupExtraHours,
      end_location_extra_hours: endLocationExtraHours,
    });
    
    // 如果是V2 transfer订单，预先计算所有车型的价格
    if (order.order_type === 'v2' && order.service_type === 'transfer' && order.pickup_area && order.dropoff_area) {
      const prices = {};
      ['alphard', 'hiace', 'premium_hiace'].forEach(vt => {
        const price = getTransferPriceForVehicle(order.pickup_area, order.dropoff_area, vt);
        if (price > 0) prices[vt] = price;
      });
      // 当前车型使用订单的base_price
      prices[normalizeVehicle(order.vehicle_type)] = order.base_price || 0;
      setEditVehiclePriceCache(prices);
    } 
    // 如果是V2 charter订单，预先计算所有车型的价格
    else if (order.order_type === 'v2' && order.service_type === 'charter') {
      const prices = {};
      // 使用目的地区域(dropoff_area)来查找价格，因为包车是按行驶区域定价
      const priceAreaCode = order.dropoff_area || order.pickup_area;
      const chargedHours = order.price_snapshot?.package_hours_charged || order.selected_hours;
      
      ['alphard', 'hiace', 'premium_hiace'].forEach(vt => {
        const price = getCharterPriceForVehicle(priceAreaCode, chargedHours, vt, order.price_snapshot);
        if (price > 0) prices[vt] = price;
      });
      // 当前车型使用订单的base_price（或price_snapshot中的package_price）
      const originalBasePrice = order.price_snapshot?.package_price || order.base_price || 0;
      prices[order.vehicle_type] = originalBasePrice;
      setEditVehiclePriceCache(prices);
    }
    else {
      setEditVehiclePriceCache({});
    }
    
    // 初始化额外服务状态
    const existingServices = order.extra_services || [];
    const serviceStates = { ...INITIAL_EDIT_EXTRA_SERVICES };
    let otherDesc = "";
    let waitingQuantity = 0;
    
    existingServices.forEach(s => {
      if (s.service_type === EXTENDED_WAITING_SERVICE_TYPE) {
        waitingQuantity = Math.max(1, Math.min(6, Number(s.quantity) || 1));
      } else if (s.service_type === 'other') {
        serviceStates.other = true;
        otherDesc = s.description || "";
      } else if (serviceStates.hasOwnProperty(s.service_type)) {
        serviceStates[s.service_type] = true;
      }
    });
    
    setEditExtraServices(serviceStates);
    setEditOtherServiceDesc(otherDesc);
    setEditExtendedWaitingQuantity(order.service_type === 'transfer' ? waitingQuantity : 0);
    setShowEditModal(true);
  };

  const canModifySupplierOrder = (order) =>
    Boolean(order?.can_modify) && order?.status !== "completed" && order?.status !== "cancelled";

  const handleSupplierModifyClick = (order) => {
    if (canModifySupplierOrder(order)) {
      handleEditOrder(order);
      return;
    }

    if (order?.status === "completed" || order?.status === "cancelled") {
      alert(
        language === "ja"
          ? "完了・キャンセル済みの予約は変更できません。"
          : language === "zh"
            ? "已完成或已取消的订单不能修改。"
            : language === "es"
              ? "Las reservas completadas o canceladas no se pueden modificar."
            : "Completed or cancelled reservations cannot be modified."
      );
      return;
    }

    alert(
      language === "ja"
        ? "この予約は現在変更できません。管理者にお問い合わせください。"
        : language === "zh"
          ? "这个订单当前不能修改，请联系管理员。"
          : language === "es"
            ? "Esta reserva no se puede modificar en este momento. Póngase en contacto con el administrador."
          : "This reservation cannot be modified right now. Please contact the administrator."
    );
  };

  // 从transfer_pricing表获取指定路线和车型的价格
  const getTransferPriceForVehicle = (pickupArea, dropoffArea, vehicleType) => {
    const normalizedType = normalizeVehicle(vehicleType);
    
    const pricing = transferPricing.find(p => 
      ((p.pickup_area === pickupArea && p.dropoff_area === dropoffArea) ||
       (p.pickup_area === dropoffArea && p.dropoff_area === pickupArea) ||
       (p.from_area === pickupArea && p.to_area === dropoffArea) ||
       (p.from_area === dropoffArea && p.to_area === pickupArea)) &&
      p.vehicle_type === normalizedType &&
      p.enabled !== false
    );
    
    return pricing ? (pricing.base_price || pricing.price || 0) : 0;
  };

  // 从charter_pricing表获取指定区域、时长和车型的价格
  const getCharterPriceForVehicle = (areaCode, chargedHours, vehicleType) => {
    const normalizedType = normalizeVehicle(vehicleType);
    
    // 先尝试精确匹配计费时长
    let pricing = charterPricing.find(p => 
      p.area_code === areaCode &&
      p.vehicle_type === normalizedType &&
      p.package_hours === chargedHours &&
      p.enabled !== false
    );
    
    // 如果没有精确匹配，找最接近的套餐
    if (!pricing) {
      const areaPackages = charterPricing.filter(p => 
        p.area_code === areaCode &&
        p.vehicle_type === normalizedType &&
        p.enabled !== false
      ).sort((a, b) => a.package_hours - b.package_hours);
      
      // 找到大于等于计费时长的最小套餐
      pricing = areaPackages.find(p => p.package_hours >= chargedHours);
      
      // 如果还没找到，用最大的套餐
      if (!pricing && areaPackages.length > 0) {
        pricing = areaPackages[areaPackages.length - 1];
      }
    }
    
    return pricing ? (pricing.price || 0) : 0;
  };

  // 计算包车价格（包括套餐匹配和超时费用）
  const calculateCharterPrice = (areaCode, selectedHours, vehicleType, pickupExtraHours = 0, endExtraHours = 0) => {
    if (!areaCode || !selectedHours || !vehicleType) {
      return { packagePrice: 0, packageHours: 0, overtimeHours: 0, overtimePrice: 0, totalPrice: 0 };
    }

    // 计算总计费时间
    const totalBillableHours = selectedHours + pickupExtraHours + endExtraHours;

    const normalizedVehicleType = normalizeVehicle(vehicleType);

    // 获取该区域和车型的所有套餐
    let availablePackages = charterPricing.filter(p => 
      p.area_code === areaCode &&
      p.vehicle_type === normalizedVehicleType &&
      p.enabled !== false
    );

    if (availablePackages.length === 0) {
      return { packagePrice: 0, packageHours: 0, overtimeHours: 0, overtimePrice: 0, totalPrice: 0, error: 'No pricing found' };
    }

    // 按小时数排序
    availablePackages.sort((a, b) => a.package_hours - b.package_hours);

    // 找到合适的套餐：小于等于计费时间的最大套餐
    let matchedPackage = null;
    for (let i = availablePackages.length - 1; i >= 0; i--) {
      if (availablePackages[i].package_hours <= totalBillableHours) {
        matchedPackage = availablePackages[i];
        break;
      }
    }

    // 如果没有找到（计费时间小于最小套餐），使用最小套餐
    if (!matchedPackage) {
      matchedPackage = availablePackages[0];
    }

    const packageHours = matchedPackage.package_hours;
    const packagePrice = matchedPackage.price || 0;

    // 计算超时时间（只有当计费时间超过套餐时间才有超时）
    const overtimeHours = Math.max(0, totalBillableHours - packageHours);
    const overtimeRate = OVERTIME_RATES[normalizedVehicleType] || OVERTIME_RATES.alphard;
    const overtimePrice = overtimeHours * overtimeRate;

    return {
      packagePrice,
      packageHours,
      overtimeHours,
      overtimeRate,
      overtimePrice,
      totalBillableHours,
      totalPrice: packagePrice + overtimePrice
    };
  };

  // 获取订单原始的合计（base_price + extra_service_total）
  const getOrderOriginalTotal = () => {
    if (!selectedOrder) return 0;
    return (selectedOrder.base_price || 0) + (selectedOrder.extra_service_total || 0);
  };

  // 计算编辑时的车辆价格（支持包车时间变更）
  const getEditVehiclePrice = () => {
    if (!selectedOrder) return 0;
    
    // V2 Charter订单：需要考虑包车时间变更
    if (selectedOrder.order_type === 'v2' && selectedOrder.service_type === 'charter') {
      const priceAreaCode = selectedOrder.dropoff_area || selectedOrder.pickup_area;
      const newSelectedHours = editForm.selected_hours || selectedOrder.selected_hours;
      const pickupExtraHours = editForm.pickup_extra_hours || 0;
      const endExtraHours = editForm.end_location_extra_hours || 0;
      
      const priceInfo = calculateCharterPrice(
        priceAreaCode,
        newSelectedHours,
        editForm.vehicle_type,
        pickupExtraHours,
        endExtraHours
      );
      
      return priceInfo.totalPrice;
    }
    
    // 如果车型没有变化且不是Charter，使用订单的原始base_price
    if (editForm.vehicle_type === selectedOrder.vehicle_type) {
      return selectedOrder.base_price || 0;
    }
    
    // V2 Transfer订单：从缓存或transfer_pricing获取价格
    if (selectedOrder.order_type === 'v2' && selectedOrder.service_type === 'transfer') {
      // 先检查缓存
      if (editVehiclePriceCache[editForm.vehicle_type]) {
        return editVehiclePriceCache[editForm.vehicle_type];
      }
      // 从transfer_pricing获取
      if (selectedOrder.pickup_area && selectedOrder.dropoff_area) {
        const price = getTransferPriceForVehicle(
          selectedOrder.pickup_area, 
          selectedOrder.dropoff_area, 
          editForm.vehicle_type
        );
        if (price > 0) return price;
      }
    }
    
    // 旧订单：从routes表获取价格
    if (selectedOrder.preset_route_id) {
      const route = routes.find(r => r.id === selectedOrder.preset_route_id);
      if (route) {
        const priceKey = `price_${editForm.vehicle_type}`;
        return route[priceKey] || 0;
      }
    }
    
    // 无法获取价格时返回0
    return 0;
  };

  // 计算编辑时的额外服务总价
  const getEditExtraServiceTotal = () => {
    let total = Number(editForm.infant_seat_count || 0) * INFANT_SEAT_PRICE;
    Object.keys(editExtraServices).forEach(key => {
      if (editExtraServices[key] && EXTRA_SERVICE_PRICES[key]) {
        total += EXTRA_SERVICE_PRICES[key];
      }
    });
    if (selectedOrder?.service_type === 'transfer') {
      total += getExtendedWaitingUnitPrice(editForm.vehicle_type) * editExtendedWaitingQuantity;
    }
    return total;
  };

  // 获取Charter订单的详细价格信息
  const getCharterPriceDetails = () => {
    if (!selectedOrder || selectedOrder.service_type !== 'charter') {
      return null;
    }
    
    const priceAreaCode = selectedOrder.dropoff_area || selectedOrder.pickup_area;
    const newSelectedHours = editForm.selected_hours || selectedOrder.selected_hours;
    const pickupExtraHours = editForm.pickup_extra_hours || 0;
    const endExtraHours = editForm.end_location_extra_hours || 0;
    
    return calculateCharterPrice(
      priceAreaCode,
      newSelectedHours,
      editForm.vehicle_type,
      pickupExtraHours,
      endExtraHours
    );
  };

  // 计算编辑时的总价
  const getEditTotalPrice = () => {
    const vehiclePrice = getEditVehiclePrice();
    const extraTotal = getEditExtraServiceTotal();
    return vehiclePrice + extraTotal;
  };

  // 检查是否需要重新报价（仅当无法获取价格时）
  const needsRequote = () => {
    if (!selectedOrder) return false;
    
    // Charter订单：检查是否能计算价格
    if (selectedOrder.service_type === 'charter') {
      const priceDetails = getCharterPriceDetails();
      return !priceDetails || priceDetails.totalPrice === 0;
    }
    
    if (editForm.vehicle_type === selectedOrder.vehicle_type) return false;
    
    // 如果能获取到价格，就不需要重新报价
    const price = getEditVehiclePrice();
    return price === 0;
  };

  // 构建额外服务数组
  const buildEditExtraServicesArray = () => {
    const services = buildInfantSeatServices(editForm.infant_seat_count);
    Object.keys(editExtraServices).forEach(key => {
      if (editExtraServices[key]) {
        if (key === 'other') {
          services.push({
            service_type: 'other',
            price: 0,
            description: editOtherServiceDesc,
            status: 'pending_approval'
          });
        } else {
          services.push({
            service_type: key,
            price: EXTRA_SERVICE_PRICES[key],
            description: null,
            status: 'approved'
          });
        }
      }
    });
    if (selectedOrder?.service_type === 'transfer' && editExtendedWaitingQuantity > 0) {
      services.push({
        service_type: EXTENDED_WAITING_SERVICE_TYPE,
        price: getExtendedWaitingUnitPrice(editForm.vehicle_type),
        quantity: editExtendedWaitingQuantity,
        status: 'approved',
      });
    }
    return services;
  };

  const handleUpdateOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const extraServicesArray = buildEditExtraServicesArray();
      
      // 构建更新数据
      const updatedData = {
        customer_name: editForm.customer_name,
        phone: editForm.phone,
        departure_date: editForm.departure_date,
        pickup_location: editForm.pickup_location,
        destination: editForm.destination,
        pickup_time: editForm.pickup_time,
        passenger_count: editForm.passenger_count,
        luggage_count: editForm.luggage_count,
        trip_details: editForm.trip_details,
        vehicle_type: editForm.vehicle_type,
        extra_services: extraServicesArray,
      };
      
      // Charter订单：添加包车时间
      if (selectedOrder.service_type === 'charter') {
        updatedData.selected_hours = editForm.selected_hours;
      }
      
      const sharedFields = ["customer_name", "phone", "departure_date", "pickup_location", "destination", "pickup_time", "selected_hours"];
      const sharedChanges = Object.fromEntries(sharedFields
        .filter(key => Object.prototype.hasOwnProperty.call(updatedData, key) && String(updatedData[key] ?? "") !== String(selectedOrder[key] ?? ""))
        .map(key => [key, updatedData[key]]));
      const hasSharedChanges = Object.keys(sharedChanges).length > 0;
      const updateWholeGroup = Boolean(selectedOrder.booking_group_id && hasSharedChanges && window.confirm(
        language === "ja"
          ? `この予約は${selectedOrder.booking_group_size}台の同一グループです。日付・時間・ルートを同じグループの全車両に反映しますか？\n「キャンセル」を選ぶと、この予約のみ変更します。`
          : language === "en"
            ? `This booking belongs to a ${selectedOrder.booking_group_size}-vehicle group. Apply the shared trip changes to every vehicle?\nChoose Cancel to update only this order.`
            : language === "es"
              ? `Esta reserva pertenece a un grupo de ${selectedOrder.booking_group_size} vehículos. ¿Aplicar los cambios comunes a todo el grupo?\nCancelar modificará solo esta reserva.`
              : `此订单属于${selectedOrder.booking_group_size}台车的同组订单。是否将日期、时间、路线等共同资料同步修改到整组？\n选择“取消”则只修改本单。`
      ));
      let groupResult = null;
      if (updateWholeGroup) {
        groupResult = await axios.put(`${API}/orders/${selectedOrder.id}/group`, sharedChanges);
        const vehicleChanges = Object.fromEntries(Object.entries(updatedData).filter(([key]) => !sharedFields.includes(key)));
        if (Object.keys(vehicleChanges).length) {
          await axios.put(`${API}/orders/${selectedOrder.id}`, vehicleChanges);
        }
      } else {
        await axios.put(`${API}/orders/${selectedOrder.id}`, updatedData);
      }
      const paymentNotice = groupResult?.data?.payment_page_needs_regeneration
        ? (language === "ja" ? "\n旧い未決済リンクは無効になりました。新しい決済リンクを作成してください。" : language === "en" ? "\nThe old unpaid payment link was invalidated. Please create a new link." : language === "es" ? "\nEl enlace de pago anterior fue invalidado. Cree uno nuevo." : "\n原未付款链接已作废，请重新生成付款链接。")
        : "";
      alert(t('updateSuccess') + paymentNotice);
      setShowEditModal(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      alert(error.response?.data?.detail || t('operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const normalizeBatchOrderItem = (entry, index) => {
    const parsed = entry?.parsed || {};
    return {
      batch_item_id: entry?.parsed?.batch_item_id || `supplier-batch-${Date.now()}-${index}`,
      raw_text: entry?.raw_text || "",
      recognized: entry?.recognized || [],
      service_type: parsed.service_type || "transfer",
      pickup_area: parsed.pickup_area || "",
      dropoff_area: parsed.dropoff_area || "",
      pickup_location_detail: parsed.pickup_location_detail || "",
      dropoff_location_detail: parsed.dropoff_location_detail || "",
      departure_date: parsed.departure_date || "",
      pickup_time: parsed.pickup_time || "",
      selected_hours: Number(parsed.selected_hours || 5),
      end_location: parsed.end_location || "",
      end_location_in_base: parsed.end_location_in_base !== undefined ? parsed.end_location_in_base : true,
      end_location_other: parsed.end_location_other || "",
      flight_number: parsed.flight_number || "",
      vehicle_type: normalizeVehicle(parsed.vehicle_type || "alphard"),
      passenger_count: Number(parsed.passenger_count || 1),
      luggage_count: Number(parsed.luggage_count || 0),
      child_seat_count: Number(parsed.child_seat_count || 0),
      customer_code: parsed.customer_code || batchCustomerCode || "",
      customer_name: parsed.customer_name || user.name || "",
      phone: parsed.phone || user.phone || "",
      trip_details: parsed.trip_details || entry?.raw_text || "",
    };
  };

  const parseSupplierBatchOrders = () => {
    const text = batchOrderText.trim();
    if (!text) {
      setBatchOrderError(batchCopy.pasteRequired);
      return;
    }
    const parsedItems = parseAdminOrderBatchText(text, areas);
    if (!parsedItems.length) {
      setBatchOrderItems([]);
      setBatchOrderError(batchCopy.noParsedOrders);
      return;
    }
    const normalized = parsedItems.map(normalizeBatchOrderItem);
    setBatchOrderItems(normalized);
    setBatchSelectedIds(normalized.filter(item=>!getBatchOrderMissingFields(item).length).map(item=>item.batch_item_id));
    setBatchView('all');
    setBatchOrderError("");
  };

  const updateBatchOrderItem = (index, patch) => {
    const next = {...batchOrderItems[index],...patch};
    if (getBatchOrderMissingFields(next).length) setBatchSelectedIds(ids=>ids.filter(id=>id!==next.batch_item_id));
    setBatchOrderItems(prev => prev.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...patch } : item
    )));
  };

  const removeBatchOrderItem = (index) => {
    const id=batchOrderItems[index]?.batch_item_id;
    setBatchSelectedIds(ids=>ids.filter(value=>value!==id));
    setBatchOrderItems(prev => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const validateBatchOrderItems = (items = batchOrderItems) => {
    const missingIndex = items.findIndex(item => getBatchOrderMissingFields(item).length > 0);
    if (missingIndex >= 0) {
      const missingFields = getBatchOrderMissingFields(items[missingIndex]);
      setBatchOrderError(batchCopy.incompleteOrder(missingIndex, missingFields));
      return false;
    }
    return true;
  };

  const submitSupplierBatchOrders = async () => {
    if (batchSubmitBusyRef.current) return;
    const submissionItems = batchAttemptRef.current?.items || selectedReadyBatchItems;
    if (!submissionItems.length) {
      setBatchOrderError(batchCopy.detectFirst);
      return;
    }
    if (submissionItems.length>30) {setBatchOrderError(({en:'Maximum 30 bookings per batch',ja:'1回につき最大30件',zh:'每批最多30个预约',es:'Máximo 30 reservas por lote'})[language]);return;}
    if (!validateBatchOrderItems(submissionItems)) return;

    batchSubmitBusyRef.current=true;
    setBatchOrderSubmitting(true);
    setBatchOrderError("");
    try {
      const idempotencyKey = batchSubmissionKeyRef.current || createIdempotencyKey();
      batchSubmissionKeyRef.current = idempotencyKey;
      batchAttemptRef.current = {items:submissionItems};
      const ordersPayload = submissionItems.map(({ batch_item_id, raw_text, recognized, ...item }) => ({
        ...item,
        pickup_time: item.pickup_time || null,
        selected_hours: item.service_type === "charter" ? Number(item.selected_hours || 5) : null,
        end_location: item.service_type === "charter" ? (item.end_location || item.dropoff_area) : null,
        end_location_in_base: item.service_type === "charter" ? Boolean(item.end_location_in_base) : true,
        end_location_other: item.service_type === "charter" ? item.end_location_other : null,
        passenger_count: Number(item.passenger_count || 1),
        luggage_count: Number(item.luggage_count || 0),
        child_seat_count: Number(item.child_seat_count || 0),
        extra_services: [],
      }));

      const response = await axios.post(`${API}/orders/v2/batch`, { orders: ordersPayload }, {
        headers: { "Idempotency-Key": idempotencyKey },
      });
      const failedCount = response.data?.failed_count || 0;
      const createdCount = response.data?.created_count || 0;
      const createdIds = (response.data?.created_orders || []).map(row=>submissionItems[row.index]?.batch_item_id).filter(Boolean);
      const remainingItems = batchOrderItems.filter(item=>!createdIds.includes(item.batch_item_id));
      batchAttemptRef.current = null;
      batchSubmissionKeyRef.current = null;
      setBatchRetryLocked(false);
      if (failedCount > 0) {
        setBatchOrderItems(remainingItems);
        setBatchSelectedIds(ids=>ids.filter(id=>!createdIds.includes(id)));
        setBatchOrderError(batchCopy.partialFailure(createdCount, failedCount));
      } else {
        alert(batchCopy.success(createdCount));
        const submittedIds = submissionItems.map(item=>item.batch_item_id);
        const unsubmitted = batchOrderItems.filter(item=>!submittedIds.includes(item.batch_item_id));
        if (unsubmitted.length) {setBatchOrderItems(unsubmitted);setBatchSelectedIds([]);fetchOrders();return;}
        setBatchOrderText("");
        setBatchOrderItems([]);
        batchSubmissionKeyRef.current = null;
        fetchOrders();
        setActiveTab("orders");
      }
    } catch (error) {
      if (!error.response || error.response.status>=500) setBatchRetryLocked(true);
      else {batchAttemptRef.current=null;batchSubmissionKeyRef.current=null;setBatchRetryLocked(false);}
      setBatchOrderError(error.response?.data?.detail || error.message || batchCopy.createFailed);
    } finally {
      batchSubmitBusyRef.current=false;
      setBatchOrderSubmitting(false);
    }
  };


  return (
    <div className={`supplier-redesign min-h-screen premium-shell ${selectedOrder && activeTab === 'orders' && !showEditModal ? 'sp-has-detail' : ''}`} data-supplier-theme={supplierTheme}>
      {/* Header */}
      <div className="premium-hero supplier-borderless-header m-4 mb-0 sm:m-6 sm:mb-0">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div className="shonan-compact-brand">
              <div className="shonan-compact-brand-mark">
                <img
                  src={shonanPalmLogo}
                  alt="Shonan International"
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="shonan-compact-brand-copy">
                <p className="shonan-compact-brand-title">SHONAN INTERNATIONAL</p>
                <p className="shonan-compact-brand-subtitle">Private Transfer & Charter</p>
              </div>
            </div>
            <div className="supplier-header-actions">
              <span className="supplier-welcome-text">
                {t('welcome')}，{user.name} ({user.customer_code})
              </span>
              <button type="button" className="sp-theme-toggle" title={workspaceCopy.theme} aria-label={workspaceCopy.theme} aria-pressed={supplierTheme === 'dark'} onClick={() => { const next = supplierTheme === 'dark' ? 'light' : 'dark'; setSupplierTheme(next); try { localStorage.setItem('shonan_supplier_theme', next); } catch {} }}>
                {supplierTheme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
              </button>
              <LanguageSelector />
              <button
                onClick={onLogout}
                className="supplier-header-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('logout')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sp-main-navigation max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="premium-toolbar grid grid-cols-1 gap-2 sm:grid-cols-4">
          <button
            onClick={() => setActiveTab("orders")}
            className={`premium-button py-3 px-4 font-semibold transition ${
              activeTab === "orders"
                ? "bg-primary text-primary-foreground shadow-[0_14px_30px_-18px_hsl(var(--primary)/0.9)]"
                : "bg-background hover:bg-card"
            }`}
            data-testid="tab-orders"
          >
            <FileText className="h-4 w-4 inline-block mr-2" />
            {t('myOrders')}
          </button>
          <button
            onClick={() => setActiveTab("newOrder")}
            className={`premium-button py-3 px-4 font-semibold transition ${
              activeTab === "newOrder"
                ? "bg-primary text-primary-foreground shadow-[0_14px_30px_-18px_hsl(var(--primary)/0.9)]"
                : "bg-background hover:bg-card"
            }`}
            data-testid="tab-new-order"
          >
            <Plus className="h-4 w-4 inline-block mr-2" />
            {t('createOrder')}
          </button>
          <button
            onClick={() => setActiveTab("batchOrder")}
            className={`premium-button py-3 px-4 font-semibold transition ${
              activeTab === "batchOrder"
                ? "bg-primary text-primary-foreground shadow-[0_14px_30px_-18px_hsl(var(--primary)/0.9)]"
                : "bg-background hover:bg-card"
            }`}
            data-testid="supplier-batch-order-tab"
          >
            <Sparkles className="h-4 w-4 inline-block mr-2" />
            {batchCopy.tab}
          </button>
          <button
            onClick={() => setActiveTab("myPage")}
            className={`premium-button py-3 px-4 font-semibold transition ${
              activeTab === "myPage"
                ? "bg-primary text-primary-foreground shadow-[0_14px_30px_-18px_hsl(var(--primary)/0.9)]"
                : "bg-background hover:bg-card"
            }`}
            data-testid="tab-my-page"
          >
            <UserRound className="h-4 w-4 inline-block mr-2" />
            {t('myPage') || 'マイページ'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="sp-main-content mx-auto mt-6 max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        {activeTab === "orders" && (
          <SupplierOrdersWorkspace orders={visibleOrders} total={orders.length} selectedIds={selectedOrderIds} selectedOrder={selectedOrder}
            toggleSelect={toggleSelectOrder} toggleAll={toggleSelectAll} onSelect={setSelectedOrder} onBulkDelete={handleBulkDelete}
            onExport={handleExportVisibleOrders} onSelectGroup={selectSupplierOrderGroup} t={t} language={language}
            getVehicleName={getVehicleName} pickup={formatPickupDisplay} destination={formatDestinationDisplay}
            paymentLabel={order => getSupplierPaymentStatusLabel(order, supplierPaymentT)}
            controls={{ status: [orderGroupFilter, setOrderGroupFilter], statusCounts: {all: filteredOrders.length, not_started: orderSections.not_started.length, ongoing: orderSections.today.length + orderSections.tomorrow.length, completed: orderSections.completed.length}, missing: [missingOnly, setMissingOnly],
              search: [orderSearchFilter, setOrderSearchFilter], date: [departureDateFilter, setDepartureDateFilter],
              dateTo: [departureDateToFilter, setDepartureDateToFilter], sort: [sortOrder, setSortOrder],
              code: [customerCodeFilter, setCustomerCodeFilter], name: [customerNameFilter, setCustomerNameFilter],
              vehicle: [vehicleTypeFilter, setVehicleTypeFilter], vehicles: vehicleFilterOptions,
              service: [serviceTypeFilter, setServiceTypeFilter], missingCount: statusOrders.filter(order => isOrderIncomplete(order)).length, hideCompleted: [hideCompleted, setHideCompleted],
              hideCancelled: [hideCancelled, setHideCancelled] }} />
        )}

        {activeTab === "newOrder" && (
          <div className="sp-new-booking-layout sp-new-booking-continuous">
          <div className="supplier-booking-page"><h1 className="sp-page-title">{t('createOrder')}</h1>
          <NewOrderFormV2
            user={user}
            orders={orders}
            onPreviewChange={setBookingPreview}
            continuousLayout
            actionsRef={newBookingActionsRef}
            onOrderCreated={() => {
              fetchOrders();
              setActiveTab("orders");
            }}
          />
          </div>
          <NewBookingSummary preview={bookingPreview} language={language} t={t} getVehicleName={getVehicleName} actionsRef={newBookingActionsRef} onBack={()=>setActiveTab('orders')}/>
          </div>
        )}

        {activeTab === "batchOrder" && (
          <div className="sp-batch-page sp-batch-redesign space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="premium-panel-title flex items-center gap-2">
                  {({en:'Batch Orders',ja:'代理店一括予約',zh:'批量预约',es:'Reservas en lote'})[language] || batchCopy.title}
                </h2>
                <p className="sr-only">
                  {batchCopy.description}
                </p>
              </div>
              <nav className="sp-batch-progress">{[batchUi.paste,batchUi.review,batchUi.create].map((label,index)=><span key={label} className={index===(batchOrderItems.length?1:0)?'is-active':''}><b>{index+1}</b>{label}{index<2 && <ChevronRight size={15}/>}</span>)}</nav>
              <div className="sr-only">
                {batchCopy.detectedCount(batchOrderItems.length)}
                {batchOrderItems.length > 0 && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {batchCopy.readySummary(batchReadyCount, batchIncompleteCount)}
                  </span>
                )}
              </div>
            </div>

            <div className="sp-batch-input-zone">
              <div className="sp-batch-paste">
              <label className="block text-sm font-semibold text-foreground">
                {batchCopy.pasteLabel}
              </label>
              <textarea
                value={batchOrderText}
                onChange={(e) => setBatchOrderText(e.target.value)}
                rows={5}
                disabled={batchOrderSubmitting || batchRetryLocked}
                className="premium-input min-h-[260px] w-full resize-y border-[#d8b769]/22 bg-black/28 shadow-none"
                placeholder={`例如：
YM-25 3名様
・出発日：2026年4月2日
・出発時間：8:00
・行程：オークラ東京 → 東照宮 → オークラ東京

YM-25 3名様
・出発日：2026年4月4日
・出発時間：8:00
・行程：オークラ東京 → 箱根 → 強羅花壇`}
              />
              </div>
              <div className="sp-batch-input-actions"><label>{batchUi.code}<input value={batchCustomerCode} disabled={batchOrderSubmitting || batchRetryLocked} onChange={event=>setBatchCustomerCode(event.target.value)} placeholder="e.g. ABC123"/></label><button type="button" className="sp-batch-apply-code" disabled={!batchCustomerCode.trim() || batchOrderSubmitting || batchRetryLocked} onClick={()=>setBatchOrderItems(items=>items.map(item=>({...item,customer_code:batchCustomerCode.trim()})))}>{batchUi.apply}</button>
              <div className="sp-batch-detect-actions">
                <button
                  type="button"
                  onClick={parseSupplierBatchOrders}
                  disabled={batchOrderSubmitting || batchRetryLocked}
                  className="premium-button bg-primary px-5 py-3 text-primary-foreground"
                >
                  {batchCopy.parseButton}
                </button>
                <button type="button" disabled={batchOrderSubmitting || batchRetryLocked} onClick={()=>{setBatchOrderText('');setBatchOrderItems([]);setBatchSelectedIds([]);setBatchOrderError('');}}><Trash2 size={15}/>{batchUi.clear}</button>
                <p className="sr-only">
                  {batchCopy.parseHint}
                </p>
              </div>
              </div>
              {batchRetryLocked && <p className="sp-batch-retry" role="alert">{batchUi.retry}</p>}
              {batchOrderError && (
                <div className="rounded-xl bg-warning/10 p-3 text-sm font-semibold text-warning">
                  {batchOrderError}
                </div>
              )}
            </div>

            {batchOrderItems.length > 0 && (
              <div className="space-y-4">
                <div
                  className="grid gap-3 rounded-2xl border border-primary/25 bg-primary/10 p-4 text-sm sm:grid-cols-3"
                  data-testid="supplier-batch-mobile-review-summary"
                >
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{batchCopy.detected}</p>
                    <p className="mt-1 text-xl font-bold text-foreground">{batchOrderItems.length}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{batchCopy.ready}</p>
                    <p className="mt-1 text-xl font-bold text-success">{batchReadyCount}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground">{batchCopy.incomplete}</p>
                    <p className="mt-1 text-xl font-bold text-warning">{batchIncompleteCount}</p>
                  </div>
                </div>
                <div className="sp-batch-table-tools"><button type="button" disabled={batchOrderSubmitting || batchRetryLocked || batchOrderItems.length>=30} onClick={()=>{const next=normalizeBatchOrderItem({},batchOrderItems.length);setBatchOrderItems(items=>[...items,next]);}}><Plus size={15}/>{batchUi.add}</button><div>{[['all',batchUi.all,batchOrderItems.length],['ready',batchUi.ready,batchReadyCount],['attention',batchUi.attention,batchIncompleteCount]].map(([key,label,count])=><button type="button" key={key} aria-pressed={batchView===key} onClick={()=>setBatchView(key)}>{label}<span>{count}</span></button>)}</div></div>
                <div className="sp-batch-table-heading"><input type="checkbox" aria-label={t('supplierSelectAll')} disabled={batchOrderSubmitting || batchRetryLocked} checked={batchReadyCount>0 && selectedReadyBatchItems.length===batchReadyCount} onChange={event=>setBatchSelectedIds(event.target.checked?batchOrderItems.filter(item=>!getBatchOrderMissingFields(item).length).map(item=>item.batch_item_id):[])}/><span>{batchUi.date}</span><span>{batchUi.route}</span><span>{batchUi.service}</span><span>{batchUi.passengers}</span><span>{batchUi.customer}</span><span>{batchUi.status}</span><span>{batchUi.edit}</span></div>
                {batchOrderItems.map((item, index) => {
                  const missingFields = getBatchOrderMissingFields(item);
                  if ((batchView==='ready' && missingFields.length) || (batchView==='attention' && !missingFields.length)) return null;
                  return (
                  <details
                    key={item.batch_item_id}
                    className={`sp-batch-entry rounded-2xl border p-4 ${missingFields.length > 0 ? "border-warning/40 bg-warning/5" : "border-border-subtle bg-card/90"}`}
                    data-testid={`supplier-batch-order-item-${index}`}
                    onToggle={event=>{if(event.currentTarget.open && !batchEditSnapshotsRef.current[item.batch_item_id]) batchEditSnapshotsRef.current[item.batch_item_id]={...item};if(!event.currentTarget.open) delete batchEditSnapshotsRef.current[item.batch_item_id];}}
                  >
                    <summary><input type="checkbox" aria-label={`${t('supplierSelectAll')} ${index+1}`} disabled={!!missingFields.length || batchOrderSubmitting || batchRetryLocked} checked={batchSelectedIds.includes(item.batch_item_id)} onClick={event=>event.stopPropagation()} onChange={event=>setBatchSelectedIds(ids=>event.target.checked?[...ids,item.batch_item_id]:ids.filter(id=>id!==item.batch_item_id))}/><span>{item.departure_date || '—'}<small>{item.pickup_time || '—'}</small></span><strong>{item.pickup_location_detail || item.pickup_area || '—'}<small>→ {item.dropoff_location_detail || item.dropoff_area || '—'}</small></strong><span>{t(item.service_type==='charter'?'supplierServiceCharter':'supplierServiceTransfer')}<small>{getVehicleName(item.vehicle_type)}</small></span><span>{item.passenger_count} / {item.luggage_count}</span><span>{item.customer_name}<small>{item.customer_code}</small></span><span className={missingFields.length ? 'sp-warning' : 'sp-paid'}>{missingFields.length ? batchUi.attention : batchUi.ready}{missingFields.length>0 && <small>{missingFields[0]}</small>}</span><span className="sp-batch-row-actions"><Pencil size={15}/><button type="button" title={batchCopy.remove} aria-label={batchCopy.remove} disabled={batchOrderSubmitting || batchRetryLocked} onClick={event=>{event.preventDefault();event.stopPropagation();removeBatchOrderItem(index);}}><Trash2 size={15}/></button></span></summary>
                    <fieldset className="sp-batch-row-editor" disabled={batchOrderSubmitting || batchRetryLocked}>
                    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-foreground">
                          {batchCopy.orderTitle(index)}
                        </h3>
                        <div className={`mt-2 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${missingFields.length > 0 ? "border border-warning/40 bg-warning/10 text-warning" : "border border-success/30 bg-success/10 text-success"}`}>
                          {missingFields.length > 0 ? batchCopy.needs(missingFields) : batchCopy.canCreate}
                        </div>
                        {item.recognized?.length > 0 && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {batchCopy.recognized(item.recognized)}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeBatchOrderItem(index)}
                        className="premium-button bg-destructive/10 px-3 py-2 text-sm text-destructive"
                      >
                        {batchCopy.remove}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.service}</span>
                        <select
                          value={item.service_type}
                          onChange={(e) => updateBatchOrderItem(index, { service_type: e.target.value })}
                          className="premium-input"
                        >
                          <option value="transfer">{batchCopy.transfer}</option>
                          <option value="charter">{batchCopy.charter}</option>
                        </select>
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.date}</span>
                        <input
                          type="date"
                          value={item.departure_date}
                          aria-invalid={!item.departure_date}
                          onChange={(e) => updateBatchOrderItem(index, { departure_date: e.target.value })}
                          className="premium-input"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.time}</span>
                        <input
                          type="time"
                          value={item.pickup_time}
                          onChange={(e) => updateBatchOrderItem(index, { pickup_time: e.target.value })}
                          className="premium-input"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.vehicle}</span>
                        <select
                          value={item.vehicle_type}
                          aria-invalid={!item.vehicle_type}
                          onChange={(e) => updateBatchOrderItem(index, { vehicle_type: e.target.value })}
                          className="premium-input"
                        >
                          <option value="alphard">Alphard</option>
                          <option value="hiace">Hiace</option>
                          <option value="premium_hiace">Premium Hiace</option>
                        </select>
                      </label>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.pickupArea}</span>
                        <select
                          value={item.pickup_area}
                          aria-invalid={!item.pickup_area}
                          onChange={(e) => updateBatchOrderItem(index, { pickup_area: e.target.value })}
                          className="premium-input"
                          data-testid={`supplier-batch-pickup-${index}`}
                        >
                          <option value="">{batchCopy.selectArea}</option>
                          {areas.map(area => (
                            <option key={area.code} value={area.code}>
                              {getLocalizedAreaName(area, language)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.dropoffArea}</span>
                        <select
                          value={item.dropoff_area}
                          aria-invalid={!item.dropoff_area}
                          onChange={(e) => updateBatchOrderItem(index, { dropoff_area: e.target.value })}
                          className="premium-input"
                          data-testid={`supplier-batch-dropoff-${index}`}
                        >
                          <option value="">{batchCopy.selectArea}</option>
                          {areas.map(area => (
                            <option key={area.code} value={area.code}>
                              {getLocalizedAreaName(area, language)}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.pickupDetail}</span>
                        <input
                          value={item.pickup_location_detail}
                          aria-invalid={!item.pickup_location_detail}
                          onChange={(e) => updateBatchOrderItem(index, { pickup_location_detail: e.target.value })}
                          className="premium-input"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.dropoffDetail}</span>
                        <input
                          value={item.dropoff_location_detail}
                          aria-invalid={!item.dropoff_location_detail}
                          onChange={(e) => updateBatchOrderItem(index, { dropoff_location_detail: e.target.value })}
                          className="premium-input"
                        />
                      </label>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.customerCode}</span>
                        <input
                          value={item.customer_code}
                          onChange={(e) => updateBatchOrderItem(index, { customer_code: e.target.value })}
                          className="premium-input"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.customerName}</span>
                        <input
                          value={item.customer_name}
                          onChange={(e) => updateBatchOrderItem(index, { customer_name: e.target.value })}
                          className="premium-input"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.passengers}</span>
                        <input
                          type="number"
                          min="1"
                          value={item.passenger_count}
                          onChange={(e) => updateBatchOrderItem(index, { passenger_count: Number(e.target.value || 1) })}
                          className="premium-input"
                        />
                      </label>
                      <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.luggage}</span>
                        <input
                          type="number"
                          min="0"
                          value={item.luggage_count}
                          onChange={(e) => updateBatchOrderItem(index, { luggage_count: Number(e.target.value || 0) })}
                          className="premium-input"
                        />
                      </label>
                    </div>

                    {item.service_type === "charter" && (
                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <label className="block">
                          <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.charterHours}</span>
                          <input
                            type="number"
                            min="1"
                            step="0.5"
                            value={item.selected_hours}
                            onChange={(e) => updateBatchOrderItem(index, { selected_hours: Number(e.target.value || 5) })}
                            className="premium-input"
                          />
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-xs font-semibold text-muted-foreground">{batchCopy.endLocation}</span>
                          <select
                            value={item.end_location || item.dropoff_area}
                            onChange={(e) => updateBatchOrderItem(index, { end_location: e.target.value })}
                            className="premium-input"
                          >
                            <option value="">{batchCopy.selectArea}</option>
                            {areas.map(area => (
                              <option key={area.code} value={area.code}>
                                {getLocalizedAreaName(area, language)}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    )}
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                      <label>{({en:'Child seats',ja:'チャイルドシート',zh:'儿童座椅',es:'Sillas infantiles'})[language]}<input type="number" min="0" max="10" value={item.child_seat_count} onChange={event=>updateBatchOrderItem(index,{child_seat_count:Math.max(0,Math.min(10,Number(event.target.value)||0))})}/></label>
                      <label>{t('phone')}<input value={item.phone} onChange={event=>updateBatchOrderItem(index,{phone:event.target.value})}/></label>
                      <label>{({en:'Flight number',ja:'便名',zh:'航班号',es:'Número de vuelo'})[language]}<input value={item.flight_number} onChange={event=>updateBatchOrderItem(index,{flight_number:event.target.value})}/></label>
                    </div>
                    <label className="mt-3 block">{({en:'Remarks',ja:'備考',zh:'备注',es:'Observaciones'})[language]}<textarea rows={2} value={item.trip_details} onChange={event=>updateBatchOrderItem(index,{trip_details:event.target.value})}/></label>
                    <div className="sp-batch-save-actions"><button type="button" className="sp-primary" onClick={event=>{delete batchEditSnapshotsRef.current[item.batch_item_id];event.currentTarget.closest('details').open=false;}}><Check size={15}/>{batchUi.save}</button><button type="button" onClick={event=>{const snapshot=batchEditSnapshotsRef.current[item.batch_item_id];if(snapshot) updateBatchOrderItem(index,snapshot);event.currentTarget.closest('details').open=false;}}>{batchUi.cancel}</button></div>
                    </fieldset>
                  </details>
                )})}

                <div className="sp-batch-create-bar"><strong><CheckCircle2 size={18}/>{selectedReadyBatchItems.length} {batchUi.selected}</strong><span>{batchUi.price}</span><span>{batchUi.pending}</span>
                  <button
                    type="button"
                    onClick={submitSupplierBatchOrders}
                    disabled={batchOrderSubmitting || selectedReadyBatchItems.length>30 || (!selectedReadyBatchItems.length && !batchRetryLocked)}
                    className="premium-button sp-primary px-6 py-3 disabled:opacity-50"
                  >
                    {batchOrderSubmitting ? batchCopy.creating : batchCopy.createOrders}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "myPage" && (
          <MyPage user={user} onUserUpdate={onUserUpdate} />
        )}
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && activeTab === 'orders' && (
        <div
          className={`sp-order-detail ${showEditModal ? 'sp-detail-hidden' : ''} fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50`}
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="sp-detail-panel premium-card max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border-subtle flex justify-between items-start">
              <div>
                <h2 className="premium-panel-title text-2xl">{t('orderDetails')}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('orderNumber')}: {getOrderNo(selectedOrder)}
                </p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                aria-label={workspaceCopy.close}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div>
                <p className="text-sm text-muted-foreground mb-2">{t('status')}</p>
                <div className="flex flex-wrap items-center gap-2">
                  <SupplierStatusBadge status={selectedOrder.status} t={t} />
                  {getSupplierPaymentStatusLabel(selectedOrder, supplierPaymentT) && (
                    <span
                      className="supplier-payment-paid inline-flex items-center gap-1 rounded-lg border border-emerald-300/25 bg-emerald-300/10 px-2 py-1 text-xs font-bold text-emerald-200"
                      data-testid="supplier-detail-payment-status"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {getSupplierPaymentStatusLabel(selectedOrder, supplierPaymentT)}
                    </span>
                  )}
                  {isSupplierImportantException(selectedOrder, isOrderIncomplete(selectedOrder)) && (
                    <span className="inline-flex items-center gap-1 rounded-lg border border-warning/30 bg-warning/10 px-2 py-1 text-xs font-bold text-warning">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {t('supplierImportantException')}
                    </span>
                  )}
                </div>
                {isOrderIncomplete(selectedOrder) && (
                  <p className="mt-2 text-xs font-semibold text-warning">
                    {t('supplierMissingField')}: {isOrderIncomplete(selectedOrder).map(getSupplierMissingFieldLabel).join(" / ")}
                  </p>
                )}
              </div>

              {/* Customer / Order Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{t('orderNumber')}</p>
                  <p className="font-semibold">{getOrderNo(selectedOrder)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('customerCode')}</p>
                  <p className="font-semibold">{selectedOrder.customer_code || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('customerName')}</p>
                  <p className="font-semibold">{selectedOrder.customer_name || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{t('phone')}</p>
                  <p className="font-semibold">{selectedOrder.phone || "-"}</p>
                </div>
              </div>

              {/* Trip Summary */}
              <div>
                <p className="text-sm text-muted-foreground mb-2">{t('supplierTripSummary')}</p>
                <div className="premium-card p-4 space-y-4">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="rounded-xl border border-border-subtle bg-background/60 p-3">
                      <p className="text-xs text-muted-foreground">{t('pickupLocation')}</p>
                      <p className="font-semibold">{formatPickupDisplay(selectedOrder) || "-"}</p>
                    </div>
                    <div className="rounded-xl border border-border-subtle bg-background/60 p-3">
                      <p className="text-xs text-muted-foreground">{t('destination')}</p>
                      <p className="font-semibold">{formatDestinationDisplay(selectedOrder) || "-"}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div className="rounded-xl border border-border-subtle bg-background/60 p-3">
                      <p className="text-xs text-muted-foreground">{t('departureDate')}</p>
                      <p className="font-semibold">{selectedOrder.departure_date || "-"}</p>
                    </div>
                    <div className="rounded-xl border border-border-subtle bg-background/60 p-3">
                      <p className="text-xs text-muted-foreground">{t('pickupTime')}</p>
                      <p className="font-semibold">{selectedOrder.pickup_time || "-"}</p>
                    </div>
                    <div className="rounded-xl border border-border-subtle bg-background/60 p-3">
                      <p className="text-xs text-muted-foreground">{t('vehicleType')}</p>
                      <p className="font-semibold">{getVehicleName(selectedOrder.vehicle_type) || "-"}</p>
                    </div>
                    <div className="rounded-xl border border-border-subtle bg-background/60 p-3">
                      <p className="text-xs text-muted-foreground">{t('passengerCount')}/{t('luggageCount')}</p>
                      <p className="font-semibold">{selectedOrder.passenger_count || 0}人 / {selectedOrder.luggage_count || 0}件</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Extra Services */}
              {selectedOrder.extra_services && selectedOrder.extra_services.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">{t('extraServices')}</p>
                  <div className="space-y-2">
                    {selectedOrder.extra_services.map((service, index) => (
                      <div key={index} className="flex justify-between premium-card p-3">
                        <span>
                          {service.service_type === EXTENDED_WAITING_SERVICE_TYPE
                            ? getExtendedWaitingDisplay(service, selectedOrder, language)
                            : `${service.description || service.service_type}${service.service_type === 'infant_seat' ? ` ×${service.quantity || 1}` : ''}`}
                        </span>
                        {service.service_type !== EXTENDED_WAITING_SERVICE_TYPE && (
                          <span className="font-bold text-success">
                            ¥{((Number(service.price) || 0) * (Number(service.quantity) || 1)).toLocaleString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <p className="text-sm text-muted-foreground mb-2">{t('supplierNotesAndContact')}</p>
                <div className="premium-card p-3 text-sm">
                  <div className="grid gap-2 md:grid-cols-2">
                    <p><span className="text-muted-foreground">{t('customerName')}:</span> {selectedOrder.customer_name || "-"}</p>
                    <p><span className="text-muted-foreground">{t('customerCode')}:</span> {selectedOrder.customer_code || "-"}</p>
                    <p><span className="text-muted-foreground">{t('phone')}:</span> {selectedOrder.phone || "-"}</p>
                    <p><span className="text-muted-foreground">{t('orderNumber')}:</span> {getOrderNo(selectedOrder)}</p>
                  </div>
                  <p className="mt-3 whitespace-pre-wrap">
                    {[selectedOrder.trip_details, selectedOrder.other_service_desc, selectedOrder.dispatch_note, selectedOrder.remarks].filter(Boolean).join("\n") || "-"}
                  </p>
                </div>
              </div>

              {/* Price */}
              {getSupplierOrderDisplayPrice(selectedOrder) > 0 && (
                <div className="premium-card p-4">
                  <p className="text-sm text-muted-foreground mb-1">{t('totalPrice')}</p>
                  <p className="text-3xl font-bold text-success">¥{getSupplierOrderDisplayPrice(selectedOrder).toLocaleString()}</p>
                </div>
              )}

            </div>
            <footer className="sp-detail-actions">
              <button type="button" onClick={() => handleSupplierModifyClick(selectedOrder)} disabled={loading}
                aria-disabled={!canModifySupplierOrder(selectedOrder)} data-testid="modify-order-btn"
                className={`premium-button bg-primary ${!canModifySupplierOrder(selectedOrder) ? 'supplier-action-button-muted' : ''}`}>{t('supplierActionModify')}</button>
              <TransportAcceptance order={selectedOrder} language={language} theme={supplierTheme} directDownload />
              <button type="button" onClick={() => selectedOrder.status !== "completed" && selectedOrder.status !== "cancelled" ? handleCancelOrder(selectedOrder.id) : null} disabled={loading || selectedOrder.status === "completed" || selectedOrder.status === "cancelled"} className="sp-cancel-action" data-testid="cancel-order-btn">{t('supplierActionCancel')}</button>
              {selectedOrder.status === 'quoted' && <button type="button" onClick={() => handleAcceptQuote(selectedOrder.id)} disabled={loading} data-testid="supplier-detail-confirm">{t('supplierActionConfirm')}</button>}
              <details className="sp-detail-more"><summary><MoreHorizontal size={18} />{({ en: 'More', zh: '更多', ja: 'その他', es: 'Más' })[language] || 'More'}</summary>
                <div>
                  <button type="button" onClick={() => handleDuplicateOrder(selectedOrder)} disabled={loading} data-testid="duplicate-order-btn">{t('supplierCopyOrder')}</button>
                  <button type="button" onClick={() => handleCreateReturnOrder(selectedOrder)} disabled={loading} data-testid="return-order-btn">{t('supplierReturnOrder')}</button>
                </div>
              </details>
            </footer>
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditModal && selectedOrder && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="premium-card max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border-subtle">
              <h2 className="premium-panel-title text-2xl">{t('modifyOrder')}</h2>
            </div>
            <form onSubmit={handleUpdateOrder} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
              <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {language === 'ja' ? '団体名' : language === 'zh' ? '团体名' : language === 'es' ? 'Código de grupo' : 'Group Code'}
                  </label>
                  <input
                    type="text"
                    value={editForm.customer_code || ''}
                    onChange={(e) => setEditForm({ ...editForm, customer_code: e.target.value })}
                    placeholder={language === 'ja' ? '例：YM-01' : language === 'zh' ? '例：YM-01' : language === 'es' ? 'Ej.: YM-01' : 'e.g. YM-01'}
                    className="premium-input"
                    data-testid="edit-customer-code"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('customerName')}
                  </label>
                  <input
                    type="text"
                    value={editForm.customer_name}
                    onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                    placeholder={language === 'es' ? 'Nombre del cliente' : language === 'en' ? 'Customer name' : '客户名字/お客様の氏名'}
                    className="premium-input"
                    data-testid="edit-customer-name"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('phone')}
                </label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="premium-input"
                  data-testid="edit-phone"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('departureDate')} *
                  </label>
                  <input
                    type="date"
                    value={editForm.departure_date}
                    onChange={(e) => setEditForm({ ...editForm, departure_date: e.target.value })}
                    required
                    className="premium-input"
                    data-testid="edit-departure-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('pickupTime')} *
                  </label>
                  <input
                    type="time"
                    value={editForm.pickup_time}
                    onChange={(e) => setEditForm({ ...editForm, pickup_time: e.target.value })}
                    required
                    className="premium-input"
                    data-testid="edit-pickup-time"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('pickupLocation')}
                </label>
                <input
                  type="text"
                  value={editForm.pickup_location}
                  onChange={(e) => setEditForm({ ...editForm, pickup_location: e.target.value })}
                  className="premium-input"
                  data-testid="edit-pickup-location"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('destination')}
                </label>
                <input
                  type="text"
                  value={editForm.destination}
                  onChange={(e) => setEditForm({ ...editForm, destination: e.target.value })}
                  className="premium-input"
                  data-testid="edit-destination"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('vehicleType')}
                  </label>
                  <select
                    value={editForm.vehicle_type || "hiace"}
                    onChange={(e) => {
                      const nextVehicleType = e.target.value;
                      setEditForm({ ...editForm, vehicle_type: nextVehicleType });
                      if (nextVehicleType !== 'hiace' && editExtraServices.hiace_14_seat_upgrade) {
                        setEditExtraServices({ ...editExtraServices, hiace_14_seat_upgrade: false });
                      }
                    }}
                    className="premium-input"
                    data-testid="edit-vehicle-type"
                  >
                    <option value="alphard">{getVehicleName('alphard')}</option>
                    <option value="hiace">{getVehicleName('hiace')}</option>
                    <option value="premium_hiace">{getVehicleName('premium_hiace')}</option>
                  </select>
                </div>
                
                {/* 包车时间 - 仅Charter订单显示 */}
                {selectedOrder.service_type === 'charter' && (
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">
                      {language === 'zh' ? '包车时间（小时）' : language === 'ja' ? 'チャーター時間（時間）' : language === 'es' ? 'Horas de chárter' : 'Charter Hours'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="24"
                      step="0.5"
                      value={editForm.selected_hours || 5}
                      onChange={(e) => setEditForm({ ...editForm, selected_hours: parseFloat(e.target.value) })}
                      className="premium-input"
                      data-testid="edit-charter-hours"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {language === 'zh' 
                        ? `原订单时间: ${selectedOrder.selected_hours || 5}小时`
                        : language === 'ja'
                        ? `元の注文時間: ${selectedOrder.selected_hours || 5}時間`
                        : language === 'es'
                        ? `Duración original: ${selectedOrder.selected_hours || 5} horas`
                        : `Original: ${selectedOrder.selected_hours || 5} hours`}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('passengerCount')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editForm.passenger_count}
                    onChange={(e) => setEditForm({ ...editForm, passenger_count: parseInt(e.target.value) })}
                    className="premium-input"
                    data-testid="edit-passenger-count"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-muted-foreground mb-2">
                    {t('luggageCount')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.luggage_count}
                    onChange={(e) => setEditForm({ ...editForm, luggage_count: parseInt(e.target.value) })}
                    className="premium-input"
                    data-testid="edit-luggage-count"
                  />
                </div>
                <InfantSeatSelector language={language} value={editForm.infant_seat_count} onChange={infant_seat_count => setEditForm({ ...editForm, infant_seat_count })} />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('tripDetailsOptional')}
                </label>
                <textarea
                  value={editForm.trip_details}
                  onChange={(e) => setEditForm({ ...editForm, trip_details: e.target.value })}
                  rows="3"
                  className="premium-input"
                  data-testid="edit-trip-details"
                />
              </div>

              {/* Extra Services Edit */}
              <div>
                <label className="block text-sm font-semibold text-muted-foreground mb-3">
                  {t('extraServices')}
                </label>
                {selectedOrder.service_type === 'transfer' && (
                  <ExtendedWaitingSelector
                    compact
                    language={language}
                    pickupArea={selectedOrder.pickup_area}
                    vehicleType={editForm.vehicle_type}
                    quantity={editExtendedWaitingQuantity}
                    onChange={setEditExtendedWaitingQuantity}
                  />
                )}
                <div className="grid grid-cols-2 gap-3">
                  <label className={`flex items-center justify-between p-3 rounded-lg border transition ${
                    editExtraServices.airport_sign ? 'border-primary/80 bg-primary/10' : 'border-border-subtle'
                  }`}>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editExtraServices.airport_sign}
                        onChange={() => setEditExtraServices({ ...editExtraServices, airport_sign: !editExtraServices.airport_sign })}
                        className="mr-3"
                      />
                      <span>{t('airportSign')}</span>
                    </div>
                    <span className="font-semibold text-success">¥2,000</span>
                  </label>

                  <label className={`flex items-center justify-between p-3 rounded-lg border transition ${
                    editExtraServices.tokyo_station_sign ? 'border-primary/80 bg-primary/10' : 'border-border-subtle'
                  }`}>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editExtraServices.tokyo_station_sign}
                        onChange={() => setEditExtraServices({ ...editExtraServices, tokyo_station_sign: !editExtraServices.tokyo_station_sign })}
                        className="mr-3"
                      />
                      <span>{t('tokyoStationSign')}</span>
                    </div>
                    <span className="font-semibold text-success">¥3,500</span>
                  </label>

                  <label className={`flex items-center justify-between p-3 rounded-lg border transition ${
                    editExtraServices.odawara_station_sign ? 'border-primary/80 bg-primary/10' : 'border-border-subtle'
                  }`}>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editExtraServices.odawara_station_sign}
                        onChange={() => setEditExtraServices({ ...editExtraServices, odawara_station_sign: !editExtraServices.odawara_station_sign })}
                        className="mr-3"
                      />
                      <span>{t('odawaraStationSign')}</span>
                    </div>
                    <span className="font-semibold text-success">¥2,500</span>
                  </label>

                  <label className={`flex items-center justify-between p-3 rounded-lg border transition ${
                    editExtraServices.hotel_sign ? 'border-primary/80 bg-primary/10' : 'border-border-subtle'
                  }`}>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editExtraServices.hotel_sign}
                        onChange={() => setEditExtraServices({ ...editExtraServices, hotel_sign: !editExtraServices.hotel_sign })}
                        className="mr-3"
                      />
                      <span>{t('hotelSign')}</span>
                    </div>
                    <span className="font-semibold text-success">無料</span>
                  </label>

                  <label className={`flex items-center justify-between p-3 rounded-lg border transition ${
                    editExtraServices.shinkansen_ticket ? 'border-primary/80 bg-primary/10' : 'border-border-subtle'
                  }`}>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editExtraServices.shinkansen_ticket}
                        onChange={() => setEditExtraServices({ ...editExtraServices, shinkansen_ticket: !editExtraServices.shinkansen_ticket })}
                        className="mr-3"
                      />
                      <span>{t('shinkansenTicket')}</span>
                    </div>
                    <span className="font-semibold text-success">¥3,000</span>
                  </label>

                  {editForm.vehicle_type === 'hiace' && (
                    <label className={`flex items-center justify-between p-3 rounded-lg border transition ${
                      editExtraServices.hiace_14_seat_upgrade ? 'border-primary/80 bg-primary/10' : 'border-border-subtle'
                    }`}>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editExtraServices.hiace_14_seat_upgrade}
                          onChange={() => setEditExtraServices({
                            ...editExtraServices,
                            hiace_14_seat_upgrade: !editExtraServices.hiace_14_seat_upgrade
                          })}
                          className="mr-3"
                        />
                        <span>
                          {language === 'ja'
                            ? '14座ハイエースアップグレード'
                            : language === 'zh'
                            ? '升级到14座海狮'
                            : language === 'es'
                            ? 'Mejora a Hiace de 14 plazas'
                            : '14-seat Hiace Upgrade'}
                        </span>
                      </div>
                      <span className="font-semibold text-success">¥10,000</span>
                    </label>
                  )}

                  <label className={`flex items-center justify-between p-3 rounded-lg border transition ${
                    editExtraServices.fuji_5th_station ? 'border-primary/80 bg-primary/10' : 'border-border-subtle'
                  }`}>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editExtraServices.fuji_5th_station}
                        onChange={() => setEditExtraServices({ ...editExtraServices, fuji_5th_station: !editExtraServices.fuji_5th_station })}
                        className="mr-3"
                      />
                      <span>
                        {language === 'ja'
                          ? '富士山五合目追加費'
                          : language === 'zh'
                          ? '富士山五合目追加费'
                          : language === 'es'
                          ? 'Suplemento por la 5.ª estación del monte Fuji'
                          : 'Mt. Fuji 5th Station Add-on'}
                      </span>
                    </div>
                    <span className="font-semibold text-success">¥10,000</span>
                  </label>

                  <label className={`flex items-center justify-between p-3 rounded-lg border transition ${
                    editExtraServices.other ? 'border-warning/80 bg-warning/15' : 'border-border-subtle'
                  }`}>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editExtraServices.other}
                        onChange={() => setEditExtraServices({ ...editExtraServices, other: !editExtraServices.other })}
                        className="mr-3"
                      />
                      <span>{t('otherService')}</span>
                    </div>
                    <span className="text-xs text-warning">{t('needsApproval')}</span>
                  </label>
                </div>

                {editExtraServices.other && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-muted-foreground mb-2">
                      {t('otherServiceDesc')}
                    </label>
                    <textarea
                      value={editOtherServiceDesc}
                      onChange={(e) => setEditOtherServiceDesc(e.target.value)}
                      rows="2"
                      className="premium-input"
                    />
                  </div>
                )}
              </div>

              {/* Price Summary - 订单合计 */}
              <div className="premium-card p-4 border border-border-subtle">
                <h3 className="font-semibold text-foreground mb-3">
                  {language === 'zh' ? '订单合计' : language === 'ja' ? '注文合計' : language === 'es' ? 'Total de la reserva' : 'Order Total'}
                </h3>
                <div className="space-y-2 text-sm">
                  {/* 显示原始订单价格 */}
                  <div className="flex justify-between text-muted-foreground">
                    <span>{language === 'zh' ? '原订单价格' : language === 'ja' ? '元の注文価格' : language === 'es' ? 'Precio original' : 'Original Price'}</span>
                    <span>¥{getSupplierOrderDisplayPrice(selectedOrder).toLocaleString()}</span>
                  </div>
                  
                  {/* Charter订单：显示原始时间 */}
                  {selectedOrder.service_type === 'charter' && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>{language === 'zh' ? '原包车时间' : language === 'ja' ? '元のチャーター時間' : language === 'es' ? 'Horas originales' : 'Original Hours'}</span>
                      <span>{selectedOrder.selected_hours || 5} {language === 'zh' ? '小时' : language === 'ja' ? '時間' : language === 'es' ? 'horas' : 'hours'}</span>
                    </div>
                  )}
                  
                  {/* 显示原始车型和基础价格 */}
                  <div className="flex justify-between text-muted-foreground">
                    <span>{language === 'zh' ? '原车型' : language === 'ja' ? '元の車種' : language === 'es' ? 'Vehículo original' : 'Original Vehicle'} ({getVehicleName(selectedOrder.vehicle_type)})</span>
                    <span>¥{(selectedOrder.base_price || 0).toLocaleString()}</span>
                  </div>
                  
                  {/* 分隔线 */}
                  <div className="border-t border-dashed border-border-subtle my-2"></div>
                  
                  {/* Charter订单：显示套餐和超时明细 */}
                  {selectedOrder.service_type === 'charter' && !needsRequote() && (() => {
                    const priceDetails = getCharterPriceDetails();
                    if (!priceDetails) return null;
                    return (
                      <>
                        {/* 新包车时间 */}
                        {editForm.selected_hours !== selectedOrder.selected_hours && (
                          <div className="flex justify-between text-primary">
                            <span>{language === 'zh' ? '新包车时间' : language === 'ja' ? '新チャーター時間' : language === 'es' ? 'Nuevas horas' : 'New Hours'}</span>
                            <span>{editForm.selected_hours} {language === 'zh' ? '小时' : language === 'ja' ? '時間' : language === 'es' ? 'horas' : 'hours'}</span>
                          </div>
                        )}
                        
                        {/* 计费明细 */}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            {language === 'zh' ? `${priceDetails.packageHours}小时套餐` : 
                             language === 'ja' ? `${priceDetails.packageHours}時間パッケージ` : 
                             language === 'es' ? `Paquete de ${priceDetails.packageHours} horas` :
                             `${priceDetails.packageHours}h Package`} ({getVehicleName(editForm.vehicle_type)})
                          </span>
                          <span className="font-medium">¥{priceDetails.packagePrice.toLocaleString()}</span>
                        </div>
                        
                        {/* 超时费用 */}
                        {priceDetails.overtimeHours > 0 && (
                          <div className="flex justify-between text-warning">
                            <span>
                              {language === 'zh' ? `超时 ${priceDetails.overtimeHours}小时 × ¥${priceDetails.overtimeRate?.toLocaleString()}/h` :
                               language === 'ja' ? `超過 ${priceDetails.overtimeHours}時間 × ¥${priceDetails.overtimeRate?.toLocaleString()}/h` :
                               language === 'es' ? `Tiempo extra ${priceDetails.overtimeHours} horas × ¥${priceDetails.overtimeRate?.toLocaleString()}/h` :
                               `Overtime ${priceDetails.overtimeHours}h × ¥${priceDetails.overtimeRate?.toLocaleString()}/h`}
                            </span>
                            <span className="font-medium">¥{priceDetails.overtimePrice.toLocaleString()}</span>
                          </div>
                        )}
                        
                        {/* 小计 */}
                        <div className="flex justify-between font-medium">
                          <span className="text-muted-foreground">
                            {language === 'zh' ? '车费小计' : language === 'ja' ? '車両費小計' : language === 'es' ? 'Subtotal del vehículo' : 'Subtotal'}
                          </span>
                          <span>¥{priceDetails.totalPrice.toLocaleString()}</span>
                        </div>
                      </>
                    );
                  })()}
                  
                  {/* 非Charter订单或需要报价时：显示简单的车辆价格 */}
                  {(selectedOrder.service_type !== 'charter' || needsRequote()) && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {editForm.vehicle_type !== selectedOrder.vehicle_type 
                          ? (language === 'zh' ? '新车型' : language === 'ja' ? '新車種' : language === 'es' ? 'Nuevo vehículo' : 'New Vehicle')
                          : (language === 'zh' ? '车辆价格' : language === 'ja' ? '車両価格' : language === 'es' ? 'Precio del vehículo' : 'Vehicle Price')} ({getVehicleName(editForm.vehicle_type)})
                      </span>
                      {needsRequote() ? (
                          <span className="text-warning font-medium">
                            {language === 'zh' ? '需要重新报价' : language === 'ja' ? '再見積もり必要' : language === 'es' ? 'Se requiere un nuevo presupuesto' : 'Requote Needed'}
                          </span>
                      ) : (
                        <span className="font-medium">¥{getEditVehiclePrice().toLocaleString()}</span>
                      )}
                    </div>
                  )}
                  
                  {/* 额外服务 */}
                  {getEditExtraServiceTotal() > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('extraServices')}</span>
                      <span className="font-medium">¥{getEditExtraServiceTotal().toLocaleString()}</span>
                    </div>
                  )}
                  
                  {/* 总价 */}
                  <div className="border-t pt-2 mt-2 flex justify-between">
                    <span className="font-bold text-lg">{t('totalPrice')}</span>
                    {needsRequote() ? (
                      <span className="font-bold text-lg text-warning">
                        {language === 'zh' ? '待报价' : language === 'ja' ? '見積もり待ち' : language === 'es' ? 'Presupuesto pendiente' : 'Pending Quote'}
                      </span>
                    ) : (
                      <span className="font-bold text-2xl text-success">
                        ¥{getEditTotalPrice().toLocaleString()}
                      </span>
                    )}
                  </div>
                  
                  {/* 价格变化提示 */}
                  {!needsRequote() && (editForm.vehicle_type !== selectedOrder.vehicle_type || 
                    (selectedOrder.service_type === 'charter' && editForm.selected_hours !== selectedOrder.selected_hours)) && (
                    <div className="mt-2 p-2 border border-warning/40 bg-warning/10 rounded text-xs text-warning">
                      {language === 'zh' 
                        ? `订单变更：${editForm.vehicle_type !== selectedOrder.vehicle_type ? `车型 ${getVehicleName(selectedOrder.vehicle_type)} → ${getVehicleName(editForm.vehicle_type)}` : ''}${editForm.vehicle_type !== selectedOrder.vehicle_type && editForm.selected_hours !== selectedOrder.selected_hours ? '，' : ''}${selectedOrder.service_type === 'charter' && editForm.selected_hours !== selectedOrder.selected_hours ? `时间 ${selectedOrder.selected_hours}h → ${editForm.selected_hours}h` : ''}，价格将更新`
                        : language === 'ja'
                        ? `注文変更あり、価格が更新されます`
                        : language === 'es'
                        ? `La reserva ha cambiado y el precio se actualizará`
                        : `Order changed, price will update`}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-6 py-3 border border-border-subtle text-foreground hover:bg-surface rounded-lg transition font-semibold"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 premium-button bg-primary text-primary-foreground transition font-semibold disabled:opacity-50"
                  data-testid="submit-edit-order"
                >
                  {loading ? t('loading') : t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
